import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { supabase } from '../data/auth';
import { looksOffline } from '../data/offline';
import type { Session } from '../data/auth';
import { cleanAuthUrl } from '../cleanAuthUrl';
import { report, отпустить } from '../sentry';
import { ГОТОВО_БЕЗ_ЭКРАНА, действие } from './serverAction';
import { AuthScreen, SessionExpired } from './AuthScreen';
import { ConfirmScreen } from './ConfirmScreen';
import type { AuthMode } from './AuthScreen';
import { PolicyScreen } from './PolicyScreen';
import { TermsScreen } from './TermsScreen';
import { RecoveryScreen } from './RecoveryScreen';
import { Hint, Screen, Title } from './kit';

/** Пауза между письмами одному адресу. Стоит на сервере
 *  (`max_frequency = "60s"`), здесь она только показывается: кнопка,
 *  отдающая отказ вместо объяснения, выглядит сломанной. */
const RESEND_PAUSE_S = 60;

/** Адрес, ждущий подтверждения, переживает перезагрузку страницы.
 *
 *  Без этого экран ввода кода жил только в памяти — и терялся ровно в тот
 *  момент, ради которого он существует: родитель уходит читать почту,
 *  браузер выгружает вкладку (на телефоне это norma, а не край), он
 *  возвращается с кодом в руках, а вводить его некуда. Запись создана,
 *  письмо ушло, войти нельзя — почта не подтверждена, — и дороги назад нет.
 *
 *  Хранится один адрес и ничего больше: ни кода, ни токена, ни пароля.
 *  Чтение и запись обёрнуты, потому что в приватном окне хранилище бросает
 *  исключение на любой доступ, а терять из-за этого весь вход нельзя. */
/** Отметка «взрослый выбрал заниматься без входа».
 *
 *  Живёт в localStorage, а не в состоянии React, потому что иначе
 *  перезагрузка вкладки возвращала бы экран входа — и ребёнок, у которого
 *  всё работало минуту назад, упирался бы в форму регистрации. Та же
 *  ошибка уже случалась с экраном ввода кода подтверждения.
 *
 *  Чтения и записи обёрнуты, потому что в приватном окне обращение к
 *  хранилищу БРОСАЕТ, а не возвращает пустоту. */
const LOCAL_ONLY_KEY = 'mathpaw.local-only';

function readLocalOnly(): boolean {
  try {
    return window.localStorage.getItem(LOCAL_ONLY_KEY) === '1';
  } catch {
    return false;
  }
}

function writeLocalOnly(value: boolean): void {
  try {
    if (value) window.localStorage.setItem(LOCAL_ONLY_KEY, '1');
    else window.localStorage.removeItem(LOCAL_ONLY_KEY);
  } catch {
    /* Приватное окно. Взрослый пройдёт этот выбор ещё раз, и это не беда. */
  }
}

const PENDING_KEY = 'mathpaw.pending-confirm';

/** Отказ сети — это отсутствие ОТВЕТА, а не ответ «сессии нет».
 *
 *  Различать обязательно, и `navigator.onLine` для этого не годится:
 *  он говорит лишь о том, что сетевой интерфейс поднят. Мёртвый Wi-Fi,
 *  портал в кафе, сломанный DNS, таймаут и пятисотка — всё это «онлайн»,
 *  и ни одно из них не означает, что сервер отозвал сессию. */
/** Экспортирован ради проверки, как `syncLine` и `shouldSync`.
 *
 *  От него зависит признак `offline`, а от признака — открывается ли на
 *  экране «войдите снова» дверь «заниматься без интернета». Ошибка в
 *  распознавании стоит либо запертого ребёнка при живом устройстве, либо
 *  обхода решения взрослого, отозвавшего доступ. Мутационный проход
 *  показал, что из пяти признаков сети проверялся один. */
export function networkFailure(error: unknown): boolean {
  const failure = error as { status?: number };
  /** Пятисотка считается сетевым отказом ТОЛЬКО здесь, и это осознанно:
   *  на заслоне входа цена ошибки — запертый снаружи родитель, поэтому
   *  «сервер прилёг» приравнивается к «связи нет» и дверь «заниматься без
   *  интернета» открывается. В строке состояния обмена наоборот: там 500
   *  обязан читаться как «сервер не отвечает», иначе родитель решит, что
   *  дело в его метро.
   *
   *  Текстовые признаки — общий список на всё приложение (`looksOffline`).
   *  Раньше их было три разных, и таймаут был сетью здесь и не был сетью в
   *  строке состояния. */
  if (typeof failure?.status === 'number' && failure.status >= 500) return true;
  return looksOffline(error);
}

function readPending(): string {
  try {
    return window.localStorage.getItem(PENDING_KEY) ?? '';
  } catch {
    return '';
  }
}

function writePending(email: string): void {
  try {
    if (email) window.localStorage.setItem(PENDING_KEY, email);
    else window.localStorage.removeItem(PENDING_KEY);
  } catch {
    /* приватное окно: подтверждение просто не переживёт перезагрузку */
  }
}

type AuthState =
  | { status: 'loading' }
  | { status: 'anon' }
  /** Войти надо заново.
   *
   *  `offline` различает два случая, которые нельзя валить в один:
   *  `true` — обновить токен не вышло ИЗ-ЗА СЕТИ, то есть сервер ничего не
   *  сказал, и занятия на устройстве по-прежнему наши;
   *  `false` — сервер ОТВЕТИЛ отказом: доступ отозван, выход сделан на всех
   *  устройствах, запись удалена.
   *
   *  Различие несущее: дверь «заниматься без интернета» открывается только
   *  в первом случае. Во втором она сводила бы на нет сам отзыв доступа —
   *  родитель отозвал потерянный планшет, а тот продолжает показывать
   *  журнал по нажатию соседней кнопки. */
  | { status: 'expired'; offline: boolean }
  | { status: 'authed'; session: Session; stale: boolean };

export interface AuthValue {
  /** `null` значит «взрослый ещё не входил».
   *
   *  Приложение при этом работает целиком: профиль заводится, занятия идут,
   *  освоенность считается. Не работает одно — обмен, потому что обмену
   *  некуда идти. Решение владельца: требовать учётную запись до первого
   *  занятия значит терять ребёнка на экране регистрации.
   *
   *  Тип нарочно допускает `null`, а не подставляет пустышку: подстановка
   *  дала бы обмену идентификатор родителя, которого нет, и занятия уехали
   *  бы в никуда молча. */
  session: Session | null;
  /** Токен протух, а обновить его не вышло из-за отсутствия сети.
   *
   *  Занятия при этом идут: журнал пишется на устройство, ребёнок ничего не
   *  замечает. Не идёт только обмен — с мёртвым токеном сервер ответит
   *  отказом на всё, и пытаться значит помечать профили сломанными на
   *  ровном месте. */
  stale: boolean;
  /** Уход в состояние «не вошли» без стирания. Стирание — отдельное
   *  действие родительского раздела, и путать их нельзя. */
  forget: () => void;
  /** Вернуть экран входа тому, кто занимался без учётной записи. */
  signIn: () => void;
}

const AuthContext = createContext<AuthValue | null>(null);

export const AuthProvider = AuthContext.Provider;

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('родительский раздел вне AuthGate');
  return value;
}

/** Обязательный вход.
 *
 *  Стоит НАД шлюзом профилей: профилей до учётной записи не существует
 *  вовсе, и показывать выбор ребёнка неаутентифицированному значит обещать
 *  занятия, которым некуда уехать.
 *
 *  Разные дороги из учётной записи ведут себя по-разному, и это несущее
 *  различие. Кнопка «Выйти» стирает данные устройства (решение владельца:
 *  приватность на общем планшете важнее сохранности неотправленного).
 *  Истёкшая или отозванная сессия НЕ стирает ничего: `supabase-js` шлёт
 *  `SIGNED_OUT` в том числе при неудачном обновлении токена, то есть при
 *  плохой связи, — и данные ребёнка исчезали бы без диалога, без числа и
 *  без взрослого рядом. */
export function AuthGate({ children }: { children: (auth: AuthValue) => ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });
  /** Состояние в ссылке — чтобы обработчики событий читали его, не
   *  пересоздаваясь и не заглядывая внутрь обновителя. */
  const stateRef = useRef(state);
  stateRef.current = state;
  const [mode, setMode] = useState<AuthMode>('signin');
  const [view, setView] = useState<'auth' | 'confirm' | 'recovery' | 'policy' | 'terms'>(
    () => (readPending() ? 'confirm' : 'auth'),
  );
  /** Адрес, на который ушёл код подтверждения. Держится отдельно от поля
   *  ввода: экран подтверждения показывает его, а форма входа к тому
   *  моменту уже размонтирована. */
  const [confirmEmail, setConfirmEmail] = useState(readPending);
  const [localOnly, setLocalOnly] = useState(readLocalOnly);
  const [recoveryStep, setRecoveryStep] = useState<'ask' | 'code'>('ask');
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Была ли сессия в этой вкладке. Отличает «ещё не входили» от
   *  «сеанс кончился»: тексты у них разные, и второй обязан говорить, что
   *  занятия целы. */
  const everAuthed = useRef(false);

  /** Последняя живая сессия. Нужна потому, что клиент при выходе стирает
   *  её из хранилища, и восстановить обмен потом нечем: у нас не остаётся
   *  ни ключа доступа, ни ключа обновления. */
  const lastSession = useRef<Session | null>(null);

  useEffect(() => {
    let alive = true;
    const auth = supabase().auth;

    /** Что означает пропавшая сессия — решается ОТВЕТОМ СЕРВЕРА, а не
     *  флагом браузера.
     *
     *  Три исхода, и они разные по смыслу:
     *
     *  - сессия обновилась — работаем дальше;
     *  - сервер отказал по существу (ключ обновления недействителен,
     *    сессия отозвана) — стена «Войдите снова». Это несущее решение
     *    про потерянный планшет: он должен закрыть журнал, как только
     *    узнает об отзыве;
     *  - ответа нет вовсе (таймаут, DNS, пятисотка, мёртвый Wi-Fi) —
     *    остаёмся внутри с мёртвым токеном. Занятия идут, обмен молчит.
     *
     *  Состояние «мёртвый токен» НЕ поглощающее: при возврате связи
     *  решение принимается заново. Иначе приложение, однажды ушедшее в
     *  офлайн, никогда бы не узнало об отзыве — стена не появилась бы
     *  даже тогда, когда должна. */
    const decide = async () => {
      const previous = lastSession.current;
      if (!previous) {
        if (alive) setState({ status: everAuthed.current ? 'expired' : 'anon', offline: false });
        return;
      }
      try {
        const { data, error } = await auth.setSession({
          access_token: previous.access_token,
          refresh_token: previous.refresh_token,
        });
        if (!alive) return;
        if (data?.session) {
          lastSession.current = data.session;
          setState({ status: 'authed', session: data.session, stale: false });
          return;
        }
        if (networkFailure(error)) {
          setState({ status: 'authed', session: previous, stale: true });
          return;
        }
        setState({ status: everAuthed.current ? 'expired' : 'anon', offline: false });
      } catch (error) {
        if (!alive) return;
        if (networkFailure(error)) {
          setState({ status: 'authed', session: previous, stale: true });
          return;
        }
        report(error, 'authDecide');
        setState({ status: everAuthed.current ? 'expired' : 'anon', offline: false });
      }
    };

    /** Холодное чтение сессии. Зовётся при запуске и при повторе, когда
     *  сессии в памяти нет: `decide` без неё умеет только регистрацию.
     *
     *  Чистка адреса — в обеих ветках, сразу после того, как клиент разобрал
     *  ссылку из письма. Одноразовый код в строке браузера уезжает в историю,
     *  в заголовок `Referer` и в повтор сессии. Бросок — не повод оставить
     *  код: `getSession` сначала дожидается разбора адреса. */
    const прочитатьСессию = () => auth.getSession().then(
      ({ data, error: cause }) => {
        cleanAuthUrl();
        if (!alive) return;
        if (data.session) {
          everAuthed.current = true;
          lastSession.current = data.session;
          setState({ status: 'authed', session: data.session, stale: false });
          return;
        }
        /** Сессии нет, но отказ похож на сетевой — значит она БЫЛА.
         *
         *  `getSession` работает от хранилища и лезет в сеть ровно за одним:
         *  обновить протухший токен. Сетевой отказ здесь невозможен, если
         *  обновлять было нечего. Значит на устройстве лежит сессия, и
         *  показывать форму регистрации — врать: занятия целы, доступ надо
         *  подтвердить.
         *
         *  Дорога, которую это чинит: планшет закрыли вечером, токен
         *  протух за ночь, утром ребёнок открыл приложение в машине без
         *  сети. Прежде он видел «Вход / Регистрация», то есть ровно тот
         *  отказ, который чинили при живой вкладке, — но по другому входу,
         *  с холодного запуска, где `lastSession` ещё пуст. */
        if (cause && networkFailure(cause)) {
          setState({ status: 'expired', offline: true });
          return;
        }
        setState({ status: 'anon' });
      },
      (cause: unknown) => {
        cleanAuthUrl();
        report(cause, 'getSession');
        if (!alive) return;
        /** Библиотека кладёт в `{ error }` только `AuthError` и перебрасывает
         *  прочее. Сетевой `TypeError` значит то же, что возвращённый отказ
         *  сети: обновлять было что. Прочий бросок сессии не доказывает. */
        if (networkFailure(cause)) {
          setState({ status: 'expired', offline: true });
          return;
        }
        setState({ status: 'anon' });
      },
    );
    отпустить(прочитатьСессию());

    const { data: sub } = auth.onAuthStateChange((event, session) => {
      if (!alive) return;
      if (session) {
        everAuthed.current = true;
        lastSession.current = session;
        setState({ status: 'authed', session, stale: false });
        return;
      }
      if (event !== 'SIGNED_OUT') return;
      отпустить(decide());


    });

    /** Связь вернулась — решаем заново. Без этого «мёртвый токен» был бы
     *  навсегда: клиент после выхода обновлять токен больше не пытается.
     *
     *  Решение принимается ИЗ ОБРАБОТЧИКА, а не изнутри обновителя
     *  состояния. StrictMode вызывает обновители дважды, и там это дало бы
     *  два одновременных `setSession` с ротацией токена обновления: один
     *  успевает, второй перетирает состояние протухшим. Правило проекта — не
     *  держать побочные действия в обновителях — заведено ровно после такой
     *  поломки с журналом попыток.
     *
     *  Состояние читается из ссылки, которая обновляется при каждой отрисовке
     *  (см. `stateRef` ниже). */
    const retry = () => {
      const current = stateRef.current;
      if (current.status === 'authed' && current.stale) отпустить(decide());
      if (current.status !== 'expired') return;
      /** Стена после живой сессии держит её в памяти — повтор спрашивает
       *  `setSession`. Стена холодного запуска сессии не получала: `decide`
       *  без неё ставит регистрацию и снимает дверь «без интернета» от
       *  одного взгляда в другое приложение. Здесь спрашиваем `getSession`
       *  заново. Пока отказ сетевой, экран остаётся тем же. */
      if (lastSession.current) отпустить(decide());
      else отпустить(прочитатьСессию());
    };
    const onOnline = retry;
    /** Возврат к вкладке — второй повод попробовать снова, и он нужен.
     *  Событие `online` не приходит, если связь не пропадала: сервер
     *  ответил 500 при живом Wi-Fi, мы пометили сессию протухшей — и без
     *  этой строки она оставалась бы протухшей до перезагрузки. */
    const onVisible = () => {
      if (document.visibilityState === 'visible') retry();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onOnline);

    return () => {
      alive = false;
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisible);
      sub.subscription.unsubscribe();
    };
  }, []);

  /** Обратный отсчёт паузы между письмами. */
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((left) => left - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  /** Возврат к экрану входа тому, кто занимался без записи. Данные при этом
   *  не трогаются: они станут принадлежать той записи, в которую войдут, —
   *  ровно то, ради чего заведено поле владельца на устройстве. */
  const signIn = useCallback(() => {
    writeLocalOnly(false);
    setLocalOnly(false);
  }, []);

  const forget = useCallback(() => {
    writePending('');
    writeLocalOnly(false);
    setLocalOnly(false);
    lastSession.current = null;
    everAuthed.current = false;
    setState({ status: 'anon' });
    setMode('signin');
    setView('auth');
    setError(null);
  }, []);

  if (state.status === 'loading') {
    return (
      <Screen>
        <Hint>Загрузка…</Hint>
      </Screen>
    );
  }

  if (state.status === 'authed') {
    return <>{children({ session: state.session, stale: state.stale, forget, signIn })}</>;
  }

  if (view === 'policy') {
    return <PolicyScreen onBack={() => setView('auth')} />;
  }
  if (view === 'terms') {
    return <TermsScreen onBack={() => setView('auth')} />;
  }

  /** Занятия без учётной записи.
   *
   *  Отметка ставится ТОЛЬКО нажатием кнопки — на экране входа или на
   *  экране «войдите снова», — и снимается при входе и при `forget`.
   *  Провалиться сюда молча нельзя, поэтому проверка стоит выше истёкшей
   *  сессии: взрослый, выбравший заниматься без сети, должен получить
   *  занятия, а не тот же экран снова.
   *
   *  Обратный порядок был бы ловушкой в другую сторону: кнопка на экране
   *  «войдите снова» не работала бы вовсе, а выглядела бы рабочей. */
  if (localOnly) {
    return <>{children({ session: null, stale: false, forget, signIn })}</>;
  }

  if (state.status === 'expired') {
    return (
      <SessionExpired
        /** Дверь наружу — только когда сервер НЕ отвечал. Если он ответил
         *  отказом, доступ отозван осознанно, и предлагать обход значило бы
         *  отменять решение взрослого одной кнопкой. */
        onLocalOnly={
          state.offline
            ? () => {
                writeLocalOnly(true);
                setLocalOnly(true);
              }
            : null
        }
        onSignIn={forget}
      />
    );
  }

  if (view === 'confirm') {
    return (
      <ConfirmScreen
        email={confirmEmail}
        cooldown={cooldown}
        busy={busy}
        error={error}
        onBack={() => {
          /** Уход по кнопке — осознанный: адрес забывается, иначе экран
           *  кода возвращался бы при каждом запуске и не давал войти. */
          writePending('');
          setView('auth');
          setError(null);
        }}
        /** Все пять действий с сервером на этом экране идут через
         *  `действие` (`./serverAction`): оно снимает занятость при ЛЮБОМ
         *  исходе и переводит отказ через `authErrorText` — и
         *  возвращённый библиотекой, и переброшенный. Прежде каждое место
         *  писало `.then(({ error }) => { setBusy(false) … })` без
         *  обработчика отказа, и бросок оставлял кнопку серой навсегда. */
        onResend={() => {
          setError(null);
          отпустить(действие({
            занять: setBusy,
            вызов: async () => {
              const ответ = await supabase().auth.resend({ type: 'signup', email: confirmEmail });
              if (ответ.error) report(ответ.error, 'resend:signup');
              return ответ;
            },
            отказ: setError,
            успех: () => setCooldown(RESEND_PAUSE_S),
            продолжение: ГОТОВО_БЕЗ_ЭКРАНА,
          }));
        }}
        onVerify={(code, email) => {
          setError(null);
          setConfirmEmail(email);
          writePending(email);
          /** `type: 'signup'` — код подтверждения адреса, а не сброса
           *  пароля. Успех сразу даёт сессию, и `onAuthStateChange` уводит
           *  на приложение: отдельного шага «теперь войдите» нет и быть не
           *  должно — родитель только что доказал, что почта его. */
          отпустить(действие({
            занять: setBusy,
            вызов: async () => {
              const ответ = await supabase().auth.verifyOtp({ email, token: code, type: 'signup' });
              /** Отказ подтверждения уходит в сбор ошибок.
               *
               *  Это экран, который решает, попадёт семья в приложение
               *  или нет, и до сих пор он молчал: родитель видел «код не
               *  подошёл», а мы не знали ни причины, ни того, что это
               *  вообще случилось. Один такой отказ уже стоил вечера —
               *  и оказалось, что до сервера запрос даже не дошёл. */
              if (ответ.error) report(ответ.error, 'verifyOtp:signup');
              return ответ;
            },
            отказ: setError,
            /** Подтвердилось — ждать больше нечего. */
            успех: () => writePending(''),
          }));
        }}
      />
    );
  }

  if (view === 'recovery') {
    return (
      <RecoveryScreen
        step={recoveryStep}
        email={recoveryEmail}
        cooldown={cooldown}
        busy={busy}
        error={error}
        onBack={() => {
          setView('auth');
          setRecoveryStep('ask');
          setError(null);
        }}
        onSend={(email) => {
          setError(null);
          отпустить(действие({
            занять: setBusy,
            вызов: () => supabase().auth.resetPasswordForEmail(email),
            отказ: setError,
            успех: () => {
              setRecoveryEmail(email);
              setRecoveryStep('code');
              setCooldown(RESEND_PAUSE_S);
            },
            продолжение: ГОТОВО_БЕЗ_ЭКРАНА,
          }));
        }}
        onVerify={(code, password) => {
          setError(null);
          отпустить(действие({
            занять: setBusy,
            /** Два вызова — одно действие: код только что дал сессию, этой
             *  сессией и меняется пароль. Разорвать эти шаги нельзя: сессия
             *  из восстановления существует ровно для смены пароля. Отказ
             *  любого из двух, возвращённый или переброшенный, приходит на
             *  экран одинаково. */
            вызов: async () => {
              const проверка = await supabase()
                .auth.verifyOtp({ email: recoveryEmail, token: code, type: 'recovery' });
              if (проверка.error) return проверка;
              return await supabase().auth.updateUser({ password });
            },
            отказ: setError,
            успех: () => {
              setView('auth');
              setRecoveryStep('ask');
            },
          }));
        }}
      />
    );
  }

  return (
    <AuthScreen
      mode={mode}
      busy={busy}
      error={error}
      // Вежливая область итога на экране входа стоит ВСЕГДА (`Notice` в
      // `AuthScreen`), хотя итога здесь сейчас нет: письмо «отправлено»
      // заменил экран ввода кода (коммит 10672db). Вставленную позже область
      // браузер не объявляет, поэтому её не убирают вместе с состоянием.
      onModeChange={(next) => {
        setMode(next);
        setError(null);
      }}
      onForgot={() => {
        setView('recovery');
        setError(null);
      }}
      /** Явная дверь на экран кода. Нужна тем, у кого код есть, а
       *  подтверждать нечем: другое устройство, другой браузер, стёртое
       *  хранилище. Без неё единственный вход туда — попытаться войти
       *  паролем и получить отказ, а догадаться до этого нельзя. */
      onHaveCode={(email) => {
        setConfirmEmail(email);
        writePending(email);
        setCooldown(0);
        setError(null);
        setView('confirm');
      }}
      onLocalOnly={() => {
        writeLocalOnly(true);
        setLocalOnly(true);
      }}
      onPolicy={() => setView('policy')}
      onTerms={() => setView('terms')}
      onSubmit={(email, password) => {
        setError(null);
        отпустить(действие({
          занять: setBusy,
          вызов: async () => {
            const auth = supabase().auth;
            const { data, error: cause } =
              mode === 'signup'
                ? await auth.signUp({ email, password })
                : await auth.signInWithPassword({ email, password });
            /** Вход отвергнут из-за неподтверждённой почты — и это НЕ
             *  тупик, каким он был, и поэтому НЕ отказ для границы: это
             *  развилка на экран кода.
             *
             *  Дорога сюда самая обыкновенная: родитель зарегистрировался
             *  на планшете ребёнка, ушёл с экрана кода (закрыл вкладку,
             *  смахнул приложение, отвлёкся на час), вернулся и попробовал
             *  войти паролем. Без этой ветки он оказывался заперт
             *  снаружи: экрана ввода кода больше не показывали, повторно
             *  зарегистрироваться нельзя, а сброс пароля — обходной путь,
             *  про который он не догадается. */
            const неподтверждена = Boolean(cause) && String(
              (cause as { message?: string }).message ?? '',
            ).toLowerCase().includes('email not confirmed');
            return {
              error: неподтверждена ? null : cause,
              сессия: data?.session ?? null,
              неподтверждена,
            };
          },
          отказ: setError,
          успех: ({ сессия, неподтверждена }) => {
            if (неподтверждена) {
              /** Возвращаем на экран кода с возможностью прислать новый:
               *  прежний к этому времени скорее всего истёк. */
              setConfirmEmail(email);
              writePending(email);
              setCooldown(0);
              setView('confirm');
              setError('Почта не подтверждена. Введите код из письма или пришлите новый.');
              return;
            }
            /** Регистрация с подтверждением почты сессии сразу не даёт. Без
             *  этой ветки экран просто ничего не делает, и родитель жмёт
             *  «Зарегистрироваться» ещё раз, упираясь в предел писем.
             *  Дальше — экран ввода кода: ссылки в письме нет намеренно, см.
             *  `ConfirmScreen`. */
            if (mode === 'signup' && !сессия) {
              setConfirmEmail(email);
              writePending(email);
              setCooldown(RESEND_PAUSE_S);
              setView('confirm');
            }
          },
          /** Регистрация с подтверждением почты не входит — «вход
           *  выполнен» здесь было бы неправдой. */
          продолжение: mode === 'signup' ? ГОТОВО_БЕЗ_ЭКРАНА : undefined,
        }));
      }}
    />
  );
}

/** Экран отсутствующей настройки. Показывается вместо белого экрана, когда
 *  сборку выложили без адреса Supabase: без сервера приложение с
 *  обязательным входом не работает вовсе. */
export function AuthUnconfigured() {
  return (
    <Screen>
      <Title>Приложение не настроено</Title>
      <Hint>
        Не задан адрес сервера. Так бывает, если сборку выложили без
        переменных окружения.
      </Hint>
    </Screen>
  );
}
